import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import { ScopedLink } from '../../components/ScopedLink';
import {
  useListStaffComplianceRegisterQuery,
  useGetExpectedItemsQuery,
  useGetLocationStaffQuery,
  useGetStaffComplianceQuery,
} from '../../store/api/localApi';
import {
  CredentialType,
  StaffComplianceRegisterEntry,
  VettingStatus,
  ClientCredentialRecord,
} from '../../types';
import { dublinDateString, withinDays } from '../../utils/date';

/**
 * The compliance register — staff x expected credential types, one row per
 * person, neutral throughout.
 *
 * The register endpoint (`GET /locations/{locationId}/staff-compliance`) is
 * a view of STAFF with records layered on: it returns one row per live staff
 * member UNION every userId with a compliance record, so a staff member with
 * nothing recorded still appears (NOT_RECORDED, zero counts) and a departed
 * staff member with records still appears in the former-staff section. No
 * client-side row synthesis is needed here any more.
 *
 * The credential-type columns come from `GET .../expected-items` — the
 * per-location config of which types this location expects — rather than
 * the full `CredentialType` enum, so a location only sees columns relevant
 * to it. A location that has never configured expected items gets a neutral
 * empty default back from the endpoint (no columns beyond vetting/references),
 * not a 404.
 *
 * The register endpoint only carries AGGREGATE counts per person
 * (credentialCount, unverifiedCredentialCount, referenceCount) plus one
 * derived vettingStatus — no per-credential-type breakdown and no dates. So
 * each row still layers in the person's full profile via
 * `useGetStaffComplianceQuery` (one call per row — location staff lists are
 * small) to get real per-credential-type cells and expiry dates for the
 * 90-day filter.
 *
 * NEUTRAL THROUGHOUT: a missing record renders "Not recorded" and nothing
 * else — no red, no warning icon, no count of what is missing. Most real
 * services migrating from paper will show "almost everything not recorded",
 * and that is the normal state, not a wall of failures.
 */

const VETTING_LABEL: Record<VettingStatus, string> = {
  [VettingStatus.NOT_RECORDED]: 'Not recorded',
  [VettingStatus.IN_PROGRESS]: 'In progress',
  [VettingStatus.VETTED]: 'Vetted',
  [VettingStatus.REVET_DUE]: 'Re-vet due',
};

const CREDENTIAL_TYPE_LABEL: Record<string, string> = {
  [CredentialType.FIRST_AID]: 'First Aid',
  [CredentialType.CHILD_PROTECTION]: 'Child Protection',
  [CredentialType.QUALIFICATION]: 'Qualification',
  [CredentialType.FIRE_SAFETY]: 'Fire Safety',
  [CredentialType.MANUAL_HANDLING]: 'Manual Handling',
  [CredentialType.OTHER]: 'Other',
};

function credentialTypeLabel(type: string): string {
  return CREDENTIAL_TYPE_LABEL[type] ?? type;
}

/**
 * One row's detail cells. Fetches the person's full profile so the grid can
 * show real per-credential-type status and dates — the register endpoint
 * only carries aggregate counts. Neutral: a cell with nothing recorded reads
 * "Not recorded" and carries no alarm styling, ever.
 *
 * ALWAYS MOUNTED, filter applied internally (`hidden` below), never excluded
 * from the parent's map before mount. Each row's expiring status is only
 * known once ITS OWN profile fetch resolves; if the parent excluded a row
 * from rendering before that fetch completed, the row would unmount and its
 * query would never get a chance to report back — permanently stranding it
 * out of the "expiring" filter regardless of its real status. Mounting every
 * row unconditionally means every profile fetch always runs to completion.
 */
const RegisterRow: React.FC<{
  entry: StaffComplianceRegisterEntry;
  credentialTypes: string[];
  today: string;
  hidden: boolean;
  onExpiringChange: (userId: string, isExpiring: boolean) => void;
}> = ({ entry, credentialTypes, today, hidden, onExpiringChange }) => {
  const { data: profile } = useGetStaffComplianceQuery(entry.userId);

  const credentialsByType = useMemo(() => {
    const map = new Map<string, ClientCredentialRecord>();
    for (const c of profile?.credentials ?? []) {
      // Last active one wins if a location somehow has duplicates recorded.
      map.set(c.credentialType, c);
    }
    return map;
  }, [profile]);

  const isExpiringSoon = useMemo(() => {
    const vettingExpiring =
      entry.vettingStatus === VettingStatus.REVET_DUE ||
      (profile?.vetting ?? []).some((v) => withinDays(v.revetDueAt, today, 90));
    const credentialExpiring = (profile?.credentials ?? []).some((c) =>
      withinDays(c.expiryDate, today, 90)
    );
    return vettingExpiring || credentialExpiring;
  }, [entry.vettingStatus, profile, today]);

  React.useEffect(() => {
    onExpiringChange(entry.userId, isExpiringSoon);
  }, [entry.userId, isExpiringSoon, onExpiringChange]);

  if (hidden) return null;

  return (
    <tr data-testid={`register-row-${entry.userId}`} className="border-b border-gray-100">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        <ScopedLink
          to={`/staff/${entry.userId}/compliance`}
          data-testid={`register-row-link-${entry.userId}`}
          className="text-primary-600 hover:text-primary-800"
        >
          {entry.staffNameSnapshot}
        </ScopedLink>
      </td>
      <td
        data-testid={`register-cell-${entry.userId}-vetting`}
        className="px-4 py-3 text-sm text-gray-700"
      >
        {VETTING_LABEL[entry.vettingStatus]}
      </td>
      {credentialTypes.map((type) => {
        const record = credentialsByType.get(type);
        return (
          <td
            key={type}
            data-testid={`register-cell-${entry.userId}-${type}`}
            className="px-4 py-3 text-sm text-gray-700"
          >
            {record ? (
              <>
                {record.verified ? 'Verified' : 'Recorded'}
                {record.expiryDate && (
                  <span className="ml-1 text-gray-500">— expires {record.expiryDate}</span>
                )}
              </>
            ) : (
              'Not recorded'
            )}
          </td>
        );
      })}
      <td
        data-testid={`register-cell-${entry.userId}-references`}
        className="px-4 py-3 text-sm text-gray-700"
      >
        {entry.referenceCount > 0 ? `${entry.referenceCount} recorded` : 'Not recorded'}
      </td>
    </tr>
  );
};

const StaffComplianceRegister: React.FC = () => {
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId } = useLocationContext();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;
  const { isAdmin } = useAuth();

  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [expiringByUserId, setExpiringByUserId] = useState<Record<string, boolean>>({});

  const { data: registerData, isLoading: isRegisterLoading } = useListStaffComplianceRegisterQuery(
    locationId ?? '',
    { skip: !locationId || !isAdmin }
  );
  const { data: staffData, isLoading: isStaffLoading } = useGetLocationStaffQuery(
    { locationId: locationId ?? '' },
    { skip: !locationId || !isAdmin }
  );
  const { data: expectedItemsData, isLoading: isExpectedItemsLoading } = useGetExpectedItemsQuery(
    locationId ?? '',
    { skip: !locationId || !isAdmin }
  );

  const handleExpiringChange = React.useCallback((userId: string, isExpiring: boolean) => {
    setExpiringByUserId((prev) =>
      prev[userId] === isExpiring ? prev : { ...prev, [userId]: isExpiring }
    );
  }, []);

  if (!isAdmin) {
    // Staff/parents never see this page — no register container, no data
    // fetch (both queries above are already skipped). A direct URL hit is
    // covered here, not just by the nav gate. Distinct testid (not merely
    // the absence of `staff-compliance-register`) so a test can assert this
    // branch positively — `toHaveCount(0)` on the loaded testid alone would
    // also pass if this whole component failed to render for an unrelated
    // reason, which proves nothing about the access check actually holding.
    return (
      <div
        data-testid="staff-compliance-register-denied"
        className="min-h-screen flex items-center justify-center text-sm text-slate-500"
      >
        You don&apos;t have access to this page.
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isRegisterLoading || isStaffLoading || isExpectedItemsLoading) {
    return (
      <div className="py-10 text-center text-sm text-gray-500">
        Loading the compliance register…
      </div>
    );
  }

  // The register endpoint (list-register handler) already unions the live
  // staff roster with every userId carrying a record, so every row — zero
  // records or many — is present here. No client-side synthesis needed.
  const register: StaffComplianceRegisterEntry[] = registerData?.register ?? [];
  const liveStaff = staffData?.staff ?? [];
  const liveStaffIds = new Set(liveStaff.map((s) => s.id));

  // Current staff come from the live membership list; former staff are
  // records whose owner has no live membership. Offboarding hard-deletes
  // membership rows, so a departed person exists only as records carrying
  // locationIdsSnapshot — that's how they got into `register` in the first
  // place (the handler unions live members with locationIdsSnapshot owners).
  const currentRows = register.filter((r) => liveStaffIds.has(r.userId));
  const formerRows = register.filter((r) => !liveStaffIds.has(r.userId));

  // Credential-type columns come from this location's expected-items config
  // rather than the full CredentialType enum, so a location only sees the
  // types it actually cares about. A location that has never configured one
  // gets a neutral empty default back from the endpoint.
  const columnTypes = expectedItemsData?.expectedItems.expectedCredentialTypes ?? [];

  // Every row in currentRows/formerRows stays MOUNTED regardless of the
  // filter (see the RegisterRow comment) — `hidden` only controls whether it
  // renders, so a row excluded by the filter still finishes its own profile
  // fetch and can report its real expiring status.
  const isRowHidden = (userId: string) => showExpiringOnly && !expiringByUserId[userId];

  const today = dublinDateString(new Date());
  const isEmpty = register.length === 0;

  return (
    <div data-testid="staff-compliance-register" className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Compliance Register</h1>
          <p className="mt-1 text-sm text-gray-600">
            Garda vetting and credentials for staff at this location. Add records as you go —
            nothing here is required to use the rest of the app.
          </p>
        </div>
        <button
          type="button"
          data-testid="register-filter-expiring"
          onClick={() => setShowExpiringOnly((v) => !v)}
          aria-pressed={showExpiringOnly}
          className={`mt-4 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium sm:mt-0 ${
            showExpiringOnly
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Expiring within 90 days
        </button>
      </div>

      {isEmpty ? (
        <div
          data-testid="register-empty-state"
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500"
        >
          No compliance records yet. Add Garda vetting, credentials, or references from a staff
          member&apos;s profile to see them here.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Staff member
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Garda vetting
                </th>
                {columnTypes.map((type) => (
                  <th
                    key={type}
                    className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {credentialTypeLabel(type)}
                  </th>
                ))}
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  References
                </th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((entry) => (
                <RegisterRow
                  key={entry.userId}
                  entry={entry}
                  credentialTypes={columnTypes}
                  today={today}
                  hidden={isRowHidden(entry.userId)}
                  onExpiringChange={handleExpiringChange}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formerRows.length > 0 && (
        <div data-testid="register-former-staff-section" className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-700">Former staff</h2>
          <p className="text-xs text-gray-500">
            People with compliance records who no longer have an active membership at this location.
            Kept for record-keeping only.
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Staff member
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Garda vetting
                  </th>
                  {columnTypes.map((type) => (
                    <th
                      key={type}
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {credentialTypeLabel(type)}
                    </th>
                  ))}
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    References
                  </th>
                </tr>
              </thead>
              <tbody>
                {formerRows.map((entry) => (
                  <RegisterRow
                    key={entry.userId}
                    entry={entry}
                    credentialTypes={columnTypes}
                    today={today}
                    hidden={isRowHidden(entry.userId)}
                    onExpiringChange={handleExpiringChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffComplianceRegister;
